import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Bell, ChevronDown, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DynamicIslandNotificationItem } from './DynamicIslandNotificationItem';
import type { DynamicIslandNotification, IslandState } from './types';
import './dynamic-island.css';

interface DynamicIslandProps {
  notifications: DynamicIslandNotification[];
  onNotificationClick?: (id: string) => void;
  onNotificationDismiss?: (id: string) => void;
  onViewAll?: () => void;
  position?: 'top' | 'bottom';
  alignment?: 'left' | 'center' | 'right';
  autoExpand?: boolean; // Auto-expand on new notification
  autoCycle?: boolean; // Auto-cycle through notifications
  cycleInterval?: number; // Milliseconds between cycles (default 5000)
  className?: string; // Custom classes for responsive visibility
}

/**
 * Dynamic Island - PREMIUM Apple-inspired notification system
 * Elite $100k design with gradient theming, multi-layer glows, and fluid animations
 */
export const DynamicIsland = memo(function DynamicIsland({
  notifications,
  onNotificationClick,
  onNotificationDismiss,
  onViewAll,
  position = 'top',
  alignment = 'center',
  autoExpand = true,
  autoCycle = true,
  cycleInterval = 5000,
  className = '',
}: DynamicIslandProps) {
  const [state, setState] = useState<IslandState>('compact');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [_isAnimating, setIsAnimating] = useState(false);
  const [hasEnteredOnce, setHasEnteredOnce] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const navigate = useNavigate();
  const cycleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dismissTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hoverDelayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const prevUnreadCountRef = useRef(0);

  const unreadNotifications = notifications.filter(n => !n.isRead);
  const unreadCount = unreadNotifications.length;
  const currentNotification = unreadNotifications[currentIndex];

  // Position classes
  const positionClasses = {
    top: 'top-4',
    bottom: 'bottom-4',
  };

  const alignmentClasses = {
    left: 'left-4',
    center: 'island-centered',
    right: 'right-4',
  };

  // Auto-expand on new notification - Only when multiple notifications
  useEffect(() => {
    if (autoExpand && unreadCount > 1 && !isHovered && state === 'compact') {
      setState('expanded');
      setIsAnimating(true);

      // Auto-dismiss after 5 seconds
      dismissTimerRef.current = setTimeout(() => {
        if (!isHovered) {
          setState('compact');
          setIsAnimating(false);
        }
      }, 5000);
    }

    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, [unreadCount, autoExpand, isHovered, state]);

  // Auto-cycle through notifications
  useEffect(() => {
    if (autoCycle && state === 'expanded' && unreadCount > 1) {
      cycleTimerRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % unreadCount);
      }, cycleInterval);
    }

    return () => {
      if (cycleTimerRef.current) {
        clearInterval(cycleTimerRef.current);
      }
    };
  }, [autoCycle, state, unreadCount, cycleInterval]);

  // Entrance animation on mount and when new notifications arrive
  useEffect(() => {
    if (unreadCount > 0) {
      // New notification arrived - trigger entrance animation
      if (unreadCount > prevUnreadCountRef.current) {
        setShouldAnimate(true);
        setHasEnteredOnce(true);

        // Reset animation state after it completes
        const timer = setTimeout(() => {
          setShouldAnimate(false);
        }, 800);

        return () => clearTimeout(timer);
      }

      if (!hasEnteredOnce) {
        setHasEnteredOnce(true);
        setShouldAnimate(true);
      }
    }

    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount, hasEnteredOnce]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (cycleTimerRef.current) {
        clearInterval(cycleTimerRef.current);
      }
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
      if (hoverDelayTimerRef.current) {
        clearTimeout(hoverDelayTimerRef.current);
      }
    };
  }, []);

  // Handle hover - Only expand when multiple notifications, with delay to prevent flicker
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);

    // Clear auto-dismiss timer
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
    }

    // Only expand if there are multiple notifications
    if (state === 'compact' && unreadCount > 1) {
      // Add small delay to prevent flicker on hover from left
      hoverDelayTimerRef.current = setTimeout(() => {
        setState('expanded');
        setIsAnimating(true);
      }, 100);
    }
  }, [state, unreadCount]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);

    // Clear hover delay timer if user leaves before expansion
    if (hoverDelayTimerRef.current) {
      clearTimeout(hoverDelayTimerRef.current);
    }

    // Auto-dismiss after leaving hover
    dismissTimerRef.current = setTimeout(() => {
      if (state !== 'menu') {
        setState('compact');
        setIsAnimating(false);
      }
    }, 2000);
  }, [state]);

  // Handle click - toggle menu
  const handleClick = useCallback(() => {
    if (state === 'menu') {
      setState('compact');
      setIsAnimating(false);
    } else if (unreadCount > 0) {
      setState('menu');
      setIsAnimating(true);
    }
  }, [state, unreadCount]);

  // Handle double click - navigate to notifications and close widget
  const handleDoubleClick = useCallback(() => {
    navigate('/notificaciones');
    setState('compact');
    setIsAnimating(false);
    onViewAll?.();
  }, [navigate, onViewAll]);

  // Handle notification click
  const handleNotificationClick = useCallback((id: string) => {
    onNotificationClick?.(id);
    setState('compact');
    setIsAnimating(false);
  }, [onNotificationClick]);

  // Handle notification dismiss
  const handleNotificationDismiss = useCallback((id: string) => {
    onNotificationDismiss?.(id);
  }, [onNotificationDismiss]);

  // Don't render if no notifications
  if (unreadCount === 0) {
    return null;
  }

  return (
    <div
      className={`fixed ${positionClasses[position]} ${alignmentClasses[alignment]} z-[100] transition-all duration-500 ${
        alignment === 'center' && state === 'compact' ? 'island-floating' : ''
      } ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        willChange: 'transform, width, height, opacity',
        animation: shouldAnimate && alignment !== 'center'
          ? 'island-entrance 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
          : 'none',
        transformOrigin: 'center center',
      }}
    >
      {/* Compact State */}
      {state === 'compact' && (
        <button
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          className={`island-glass island-hover-lift rounded-full flex items-center justify-center gap-3 transition-all duration-500 ${
            unreadCount > 0 ? 'island-glow' : ''
          }`}
          style={{
            width: '140px',
            height: '44px',
            padding: '0 16px',
          }}
          aria-label={`${unreadCount} notificaciones sin leer. Click para expandir, doble click para ver todas`}
        >
          <div className="relative">
            <Bell className="w-5 h-5 text-white island-icon-glow" strokeWidth={2.5} />
            {/* Pulse dot indicator */}
            <span
              className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-white"
              style={{
                animation: 'island-dot-pulse 2s ease-in-out infinite',
                boxShadow: '0 0 8px rgba(255, 255, 255, 0.8)',
              }}
              aria-hidden="true"
            />
          </div>
          <span className="text-white text-base font-bold tracking-wide island-badge-pulse">
            {unreadCount}
          </span>
          <Sparkles className="w-4 h-4 text-white/80" strokeWidth={2} />
        </button>
      )}

      {/* Expanded State - Single Notification */}
      {state === 'expanded' && currentNotification && (
        <div
          className="island-glass rounded-3xl island-hover-lift cursor-pointer"
          style={{
            width: window.innerWidth < 768 ? '95vw' : '420px',
            maxWidth: '420px',
            padding: '20px',
            transition: 'width 0.5s ease, padding 0.5s ease, max-width 0.5s ease',
          }}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          aria-label="Ver más notificaciones"
        >
          <DynamicIslandNotificationItem
            notification={currentNotification}
            onClick={handleNotificationClick}
            onDismiss={handleNotificationDismiss}
          />

          {/* Cycle indicator */}
          {unreadCount > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-white/20">
              {unreadNotifications.slice(0, 5).map((_, idx) => (
                <div
                  key={idx}
                  className="transition-all duration-300 rounded-full"
                  style={{
                    width: idx === currentIndex ? '24px' : '8px',
                    height: '8px',
                    backgroundColor: idx === currentIndex
                      ? 'rgba(255, 255, 255, 0.95)'
                      : 'rgba(255, 255, 255, 0.35)',
                    boxShadow: idx === currentIndex
                      ? '0 0 12px rgba(255, 255, 255, 0.6)'
                      : 'none',
                  }}
                  aria-hidden="true"
                />
              ))}
              {unreadCount > 5 && (
                <span className="text-xs text-white/80 font-semibold ml-2">
                  +{unreadCount - 5}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Menu State - List of Notifications */}
      {state === 'menu' && (
        <div
          className="island-glass rounded-3xl flex flex-col"
          style={{
            width: window.innerWidth < 768 ? '95vw' : '420px',
            maxWidth: '420px',
            maxHeight: window.innerHeight < 800 ? '85vh' : '650px',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4 border-b border-white/20 flex-shrink-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02))',
              animation: 'fadeIn 0.3s ease-out 0.2s backwards',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="text-base font-bold text-white tracking-tight">
                Notificaciones
              </h3>
              <span
                className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-white/25 text-white"
                style={{
                  boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.3)',
                }}
              >
                {unreadCount}
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDoubleClick();
              }}
              className="text-sm text-white/90 hover:text-white transition-colors flex items-center gap-1.5 font-semibold hover:bg-white/10 px-3 py-1.5 rounded-full"
            >
              Ver todas
              <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>

          {/* Notification List */}
          <div
            className="flex-1 overflow-y-auto island-scrollbar"
            style={{
              minHeight: 0,
              animation: 'fadeIn 0.3s ease-out 0.25s backwards',
            }}
          >
            {unreadNotifications.slice(0, 5).map((notification, index) => (
              <div
                key={notification.id}
                style={{
                  borderBottom: index < Math.min(unreadNotifications.length, 5) - 1
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : 'none',
                }}
              >
                <DynamicIslandNotificationItem
                  notification={notification}
                  isCompact
                  onClick={handleNotificationClick}
                  onDismiss={handleNotificationDismiss}
                />
              </div>
            ))}
          </div>

          {/* Footer */}
          {unreadCount > 5 && (
            <div
              className="px-6 py-4 border-t border-white/20 flex-shrink-0"
              style={{
                background: 'linear-gradient(to top, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02))',
                animation: 'fadeIn 0.3s ease-out 0.3s backwards',
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDoubleClick();
                }}
                className="w-full py-3 text-sm text-white font-semibold hover:bg-white/15 transition-all rounded-xl flex items-center justify-center gap-2"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15)',
                }}
              >
                Ver {unreadCount - 5} notificaciones más
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
